import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// ПЕРЕКЛЮЧАТЕЛЬ НАСТРОЕНИЯ САЙТА: САКУРА ↔ МОМИДЗИ.
//
// В Японии сезон — это не погода, а способ смотреть на мир: весной ездят за
// цветением сакуры (ханами), осенью — за красными клёнами (момидзи-гари).
// Сайт умеет и то, и другое, поэтому даёт выбрать настроение: одна кнопка
// перекрашивает страницу целиком — фон, карточки, рамки, цены, лепестки.
//
// КАК ЭТО УСТРОЕНО. Вся палитра лежит в переменных --kj-* (src/index.css),
// а Tailwind на них только ссылается. Переключатель ставит на <html>
// атрибут data-season — и браузер сам перекрашивает всё, что использует
// эти переменные. Ни один компонент об этом не знает и не перерисовывается.
//
// ПО УМОЛЧАНИЮ — по календарю: с марта по август сайт весенний, с сентября
// по февраль осенний. Выбор человека важнее календаря и переживает
// перезагрузку (localStorage).

const STORAGE_KEY = "kj-season";
export const SEASONS = ["sakura", "momiji"];

/** Сезон по календарю: март–август — сакура, сентябрь–февраль — момидзи. */
const seasonByMonth = () => {
  const month = new Date().getMonth(); // 0 = январь
  return month >= 2 && month <= 7 ? "sakura" : "momiji";
};

const readStored = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return SEASONS.includes(saved) ? saved : null;
  } catch {
    // приватный режим в Safari запрещает localStorage — не повод падать
    return null;
  }
};

/**
 * Применить сезон к документу. Вынесено отдельно, потому что вызывается и
 * из React, и из инлайн-скрипта в public/index.html — там сезон ставится
 * ДО первой отрисовки, чтобы страница не моргнула чужой палитрой.
 */
export const applySeason = (season) => {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.season = season;
};

export default function SeasonSwitcher({ className = "", compact = false }) {
  const { t } = useTranslation();
  const [season, setSeason] = useState(() =>
    typeof window === "undefined" ? "sakura" : readStored() || seasonByMonth()
  );

  // Первая установка: если инлайн-скрипт не сработал, ставим сезон здесь.
  useEffect(() => {
    applySeason(season);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = useCallback((next) => {
    setSeason(next);
    applySeason(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* приватный режим — просто не запоминаем */
    }

    // Плавность включаем только на время самой смены: постоянный transition
    // на всех элементах замедлил бы каждый ховер на сайте.
    const root = document.documentElement;
    root.classList.add("season-changing");
    window.clearTimeout(root._seasonTimer);
    root._seasonTimer = window.setTimeout(
      () => root.classList.remove("season-changing"),
      600
    );
  }, []);

  const other = season === "sakura" ? "momiji" : "sakura";

  return (
    <button
      type="button"
      onClick={() => change(other)}
      aria-label={t(`season.switchTo.${other}`)}
      title={t(`season.switchTo.${other}`)}
      className={`group inline-flex items-center gap-2 rounded-full border border-kj-line bg-kj-dark/40 backdrop-blur-sm px-3 min-h-[38px] text-kj-sub transition-colors hover:border-kj-gold hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark ${className}`}
    >
      {/* Знак сезона: цветок сакуры или кленовый лист, оба в цвет акцента */}
      <span aria-hidden="true" className="flex-none">
        {season === "sakura" ? (
          <svg viewBox="-12 -12 24 24" className="h-4 w-4" fill="none">
            {[0, 72, 144, 216, 288].map((a) => (
              <path
                key={a}
                d="M0 0 C-3.6 -2.4 -4.3 -7.7 -1.6 -9.9 L0 -8.7 L1.6 -9.9 C4.3 -7.7 3.6 -2.4 0 0 Z"
                transform={`rotate(${a})`}
                className="fill-kj-gold"
              />
            ))}
          </svg>
        ) : (
          <svg viewBox="-12 -12 24 24" className="h-4 w-4" fill="none">
            <path
              d="M0 10 L0 2 M0 2 L-1.4 -1 L-6.5 0 L-4.7 -3 L-10 -5.5 L-5.4 -6.7
                 L-7 -11.5 L-2.6 -9 L0 -14 L2.6 -9 L7 -11.5 L5.4 -6.7 L10 -5.5
                 L4.7 -3 L6.5 0 L1.4 -1 Z"
              className="fill-kj-gold stroke-kj-gold"
              strokeWidth="0.6"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      {!compact && (
        <span className="text-xs font-bold uppercase tracking-[0.12em] whitespace-nowrap">
          {t(`season.${season}`)}
        </span>
      )}
    </button>
  );
}
